import { ComponentProps, FC, FunctionComponent } from "react";
import { immer } from "zustand/middleware/immer";

import { ToastProps } from "@/shared/ui/toast/toast.types";
import { createStore } from "@/utils";

type UIState = {
  toast: { isOpened: boolean; data: ToastProps | null; duration?: number };
  modal: { isOpened: boolean; fullHeight?: boolean; component: FC | null; data: any };
  drawer: { isOpened: boolean; component: FC | null; data: any };
};

type UIActions = {
  actions: {
    toast: { openToast: (data: ToastProps, duration?: number) => void; closeToast: () => void };
    modal: {
      openModal: <T extends FunctionComponent<any>>(
        component: T,
        data: ComponentProps<T>,
        fullHeight?: boolean
      ) => void;
      closeModal: () => void;
    };
    drawer: {
      openDrawer: <T extends FunctionComponent<any>>(component: T, data: ComponentProps<T>) => void;
      closeDrawer: () => void;
    };
  };
};

const defaultUIValues: UIState = {
  toast: { isOpened: false, data: null },
  modal: { isOpened: false, component: null, data: null },
  drawer: { isOpened: false, component: null, data: null },
};

const useUIStore = createStore<UIState & UIActions>(
  immer((set) => ({
    ...defaultUIValues,
    actions: {
      toast: {
        openToast: (toastData, duration) =>
          set((state) => {
            state.toast.isOpened = true;
            state.toast.data = toastData;
            state.toast.duration = duration;
          }),
        closeToast: () =>
          set((state) => {
            state.toast.isOpened = false;
          }),
      },
      modal: {
        openModal: (component, data, fullHeight) =>
          set((state) => {
            state.modal.isOpened = true;
            state.modal.component = component;
            state.modal.data = data;
            state.modal.fullHeight = fullHeight;
          }),
        closeModal: () =>
          set((state) => {
            state.modal.isOpened = false;
            state.modal.component = null;
            state.modal.data = null;
          }),
      },
      drawer: {
        openDrawer: (component, data) =>
          set((state) => {
            state.drawer.isOpened = true;
            state.drawer.component = component;
            state.drawer.data = data;
          }),
        closeDrawer: () =>
          set((state) => {
            state.drawer.isOpened = false;
          }),
      },
    },
  }))
);

export { useUIStore };
